import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { fetchFlows, deleteFlow, updateFlow, invokeFlow, fetchFlowsSummary } from '../../../Redux/ActionCreators/onboardingAction';
import CreateFlow from '../CreateFlow/CreateFlow';
import '../onboarding.scss';
import { Table } from "react-bootstrap";

const normalizeStatus = (s) => {
  if (s === 'active') return 'active';
  if (s === 'draft') return 'draft';
  return 'inactive';
};

const statusConfig = {
  active:   { cls: 'ob-badge--green', label: 'Published' },
  draft:    { cls: 'ob-badge--amber', label: 'Draft' },
  inactive: { cls: 'ob-badge--gray',  label: 'Inactive' },
};

const FLOW_SUMMARY_COLORS = {
  active:   { border: '#16a34a', color: '#16a34a', bg: 'rgba(22,163,74,0.08)' },
  draft:    { border: '#d97706', color: '#b45309', bg: 'rgba(217,119,6,0.08)' },
  inactive: { border: '#e4e9f0', color: '#8a9199', bg: '#f4f5f7' },
};

const FlowSummaryStrip = ({ summary, activeStatus, onStatusClick, onClear }) => {
  if (!summary?.statuses?.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '0 0 12px', padding: '10px 14px', border: '1px solid #e4e9f0', borderRadius: 10, background: '#fff' }}>
      <span
        onClick={onClear}
        style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, cursor: 'pointer', border: `1px solid ${!activeStatus ? '#f75891' : '#e4e9f0'}`, background: !activeStatus ? '#f75891' : '#f4f5f7', color: !activeStatus ? '#fff' : '#1a1a2e' }}
      >
        All <strong>{summary.total_count ?? 0}</strong>
      </span>
      {summary.statuses.map(({ status, label, count }) => {
        const isActive = activeStatus === status;
        const colors = FLOW_SUMMARY_COLORS[status] || FLOW_SUMMARY_COLORS.inactive;
        const isEmpty = (count ?? 0) === 0;
        const displayLabel = status === 'inactive' ? 'Inactive' : label;
        return (
          <span
            key={status}
            onClick={() => isActive ? onClear() : onStatusClick(status)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, padding: '4px 10px', borderRadius: 999,
              border: `1px solid ${isActive ? colors.border : '#e4e9f0'}`,
              background: isActive ? colors.bg : '#f4f5f7',
              color: isActive ? colors.color : isEmpty ? '#9aa3b0' : '#525b66',
              cursor: 'pointer', whiteSpace: 'nowrap',
              opacity: isEmpty && !isActive ? 0.55 : 1,
            }}
          >
            {displayLabel}
            <strong style={{ color: isActive ? colors.color : isEmpty ? '#9aa3b0' : '#1a1a2e', fontWeight: 700 }}>{count ?? 0}</strong>
          </span>
        );
      })}
      {activeStatus && (
        <button onClick={onClear} style={{ marginLeft: 'auto', fontSize: 11.5, color: '#8a9199', background: 'none', border: '1px solid transparent', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>
          ✕ Clear filter
        </button>
      )}
    </div>
  );
};

const getStepCount = (flow) => {
  const sf = flow.step_flags || {};
  let count = 1;
  if (sf.welcome !== false) count++;
  if (sf.forms !== false) count++;
  if (sf.documents !== false) count++;
  return count;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const flowUsedCount = (flow) => (typeof flow.used_count === 'number' ? flow.used_count : 0);
const flowCanDelete = (flow, usedCount) => (typeof flow.can_delete === 'boolean' ? flow.can_delete : usedCount === 0);

const renderCustomPagination = (totalPages, currentPage, setCurrentPage) => {
  const pages = [];
  pages.push(
    <button className="prev-btn" key="prev" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
      <img src="/images/leftarrow-icon.svg" alt="" />
    </button>
  );
  pages.push(
    <button key={1} className={currentPage === 1 ? 'active' : ''} onClick={() => setCurrentPage(1)}>{1}</button>
  );
  if (currentPage > 3) pages.push(<button key="left-dots">...</button>);
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) {
    pages.push(<button key={i} className={currentPage === i ? 'active' : ''} onClick={() => setCurrentPage(i)}>{i}</button>);
  }
  if (currentPage < totalPages - 2) pages.push(<button key="right-dots">...</button>);
  if (totalPages > 1) {
    pages.push(
      <button key={totalPages} className={currentPage === totalPages ? 'active' : ''} onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
    );
  }
  pages.push(
    <button className="next-btn" key="next" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>
      <img src="/images/leftarrow-icon.svg" alt="" style={{ rotate: "180deg" }} />
    </button>
  );
  return pages;
};

const FlowsList = () => {
  const dispatch = useDispatch();

  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editFlow, setEditFlow] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: 10 });
  const [flowsSummary, setFlowsSummary] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const loadFlows = useCallback(async (page, size, status, searchTerm = '') => {
    setLoading(true);
    const paramParts = [];
    if (status !== 'all') paramParts.push(`status=${status}`);
    if (searchTerm) paramParts.push(`search=${encodeURIComponent(searchTerm)}`);
    const params = paramParts.join('&');
    const [res, summaryRes] = await Promise.all([
      dispatch(fetchFlows(page, size, params)),
      dispatch(fetchFlowsSummary()),
    ]);
    if (res) {
      setFlows(res.data || []);
      setPagination(res.pagination || { total: (res.data || []).length, page, page_size: size });
    }
    if (summaryRes) {
      setFlowsSummary(summaryRes?.data || summaryRes);
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => { loadFlows(currentPage, pageSize, statusFilter, debouncedSearch); }, [loadFlows, currentPage, pageSize, statusFilter, debouncedSearch]);

  const normalized = flows.map(f => ({ ...f, _status: normalizeStatus(f.status) }));

  const filtered = normalized;

  const totalPages = Math.ceil((pagination.total || 0) / pageSize);

  const stats = {
    total: pagination.total || 0,
    active: flows.filter(f => normalizeStatus(f.status) === 'active').length,
    draft: flows.filter(f => normalizeStatus(f.status) === 'draft').length,
  };

  const handleDelete = async () => {
    setDeleting(true);
    await dispatch(deleteFlow(deleteId));
    setDeleteId(null);
    setDeleting(false);
    loadFlows(currentPage, pageSize, statusFilter, debouncedSearch);
  };

  const handleSetStatus = async (flowId, newStatus) => {
    setUpdatingId(flowId);
    await dispatch(updateFlow(flowId, { status: newStatus }));
    setUpdatingId(null);
    loadFlows(currentPage, pageSize, statusFilter, debouncedSearch);
  };

  const handleTestFlow = async (flow) => {
    setTestingId(flow.id);
    const res = await dispatch(invokeFlow({
      flow_id: flow.id,
      initiation_type: 'test',
      skip_otp: true,
      skip_invite_email: true,
      use_recruiter_identity: true,
      joining_date: new Date().toISOString().split('T')[0],
    }));
    setTestingId(null);
    if (res) {
      const data = res?.data ?? res;
      const token = data?.portal_session?.access_token;
      if (token) {
        const url = `/onboarding/test-flow?token=${encodeURIComponent(token)}&name=${encodeURIComponent(flow.name)}`;
        window.open(url, '_blank');
      } else {
        alert('Could not start test session. Please try again.');
      }
    }
  };

  const handleFlowSaved = () => {
    setShowCreate(false);
    setEditFlow(null);
    loadFlows(currentPage, pageSize, statusFilter, debouncedSearch);
  };

  if (showCreate || editFlow) {
    return (
      <CreateFlow
        flowData={editFlow}
        onBack={() => { setShowCreate(false); setEditFlow(null); }}
        onSaved={handleFlowSaved}
      />
    );
  }

  return (
    <div className="ob-page">
      <div className="ob-page__header">
        <div className="ob-page__header-left">
          <h2>Onboarding Flows</h2>
          <p>Create and manage structured onboarding workflows for new hires</p>
        </div>
        <button className="ob-btn ob-btn--primary" onClick={() => setShowCreate(true)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New Flow
        </button>
      </div>

      <div className="ob-card" style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
        <input
          className="ob-search-input"
          style={{ flex: 1, padding: '9px 12px', border: '1px solid #e4e9f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
          placeholder="Search flows…"
          value={search}
          onChange={e => {
            const val = e.target.value;
            setSearch(val);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
              setDebouncedSearch(val);
              setCurrentPage(1);
            }, 400);
          }}
        />
        <select
          style={{ padding: '9px 12px', border: '1px solid #e4e9f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', cursor: 'pointer', background: '#fff', color: '#525b66' }}
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">All Statuses</option>
          <option value="active">Published</option>
          <option value="draft">Draft</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <FlowSummaryStrip
        summary={flowsSummary}
        activeStatus={statusFilter === 'all' ? null : statusFilter}
        onStatusClick={(status) => { setStatusFilter(status); setCurrentPage(1); }}
        onClear={() => { setStatusFilter('all'); setCurrentPage(1); }}
      />

      {loading ? (
        <div className="ob-spinner"><div className="spinner-border" role="status" /></div>
      ) : filtered.length === 0 ? (
        <div className="ob-empty">
          <span className="ob-empty__icon">📋</span>
          <div className="ob-empty__title">{search ? 'No matching flows' : 'No flows yet'}</div>
          <div className="ob-empty__sub">
            {search ? 'Try a different search term.' : 'Create your first onboarding flow to get started.'}
          </div>
          {!search && (
            <button className="ob-btn ob-btn--primary" onClick={() => setShowCreate(true)}>
              Create Flow
            </button>
          )}
        </div>
      ) : (
        <div className="ob-table-wrap">
          <Table responsive className="ob-table">
            <thead>
              <tr>
                <th>Flow Name</th>
                <th>HR</th>
                <th>Status</th>
                <th>Steps</th>
                <th>Used</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(flow => {
                const sc = statusConfig[flow._status] || statusConfig.inactive;
                const isBusy = updatingId === flow.id || testingId === flow.id;
                const usedCount = flowUsedCount(flow);
                const canDelete = flowCanDelete(flow, usedCount);
                return (
                  <tr key={flow.id}>
                    <td>
                      <div className="td-name">{flow.name}</div>
                      {flow.description && <div className="td-sub">{flow.description}</div>}
                    </td>
                    <td>
                      <div className="td-name">{flow.hr_assignee_name}</div>
                      {flow.hr_assignee_email && <div className="td-sub">{flow.hr_assignee_email}</div>}
                    </td>
                    <td>
                      <span className={`ob-badge ${sc.cls}`}>{sc.label}</span>
                    </td>
                    <td>
                      <span style={{ fontSize: 13, color: '#525b66' }}>{getStepCount(flow)}</span>
                    </td>
                    <td>
                      {usedCount > 0 ? (
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#1e40af'}}>
                          {usedCount}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, color: '#9aa3b0' }}>0</span>
                      )}
                    </td>
                    <td>
                      <span style={{ fontSize: 12.5, color: '#8a9199' }}>
                        {flow.updated_at ? formatDate(flow.updated_at) : formatDate(flow.created_at)}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 5 }}>
                        {/* Test Portal: show only when active */}
                        {flow._status === 'active' && (
                          <button
                            className="ob-btn ob-btn--sm ob-btn--icon-only"
                            style={{ color: '#1e40af', borderColor: '#bfdbfe', background: '#eff6ff' }}
                            onClick={() => handleTestFlow(flow)}
                            disabled={isBusy}
                            title="Preview candidate portal"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                              <polygon points="5 3 19 12 5 21 5 3"/>
                            </svg>
                          </button>
                        )}
                        {/* Edit */}
                        <button
                          className="ob-btn ob-btn--sm ob-btn--icon-only"
                          onClick={() => setEditFlow(flow)}
                          disabled={isBusy}
                          title="Edit"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        {/* Delete / Archive */}
                        {canDelete ? (
                          <button
                            className="ob-btn ob-btn--sm ob-btn--icon-only ob-btn--danger"
                            onClick={() => setDeleteId(flow.id)}
                            disabled={isBusy}
                            title="Delete flow"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14H6L5 6"/>
                              <path d="M10 11v6M14 11v6"/>
                              <path d="M9 6V4h6v2"/>
                            </svg>
                          </button>
                        ) : flow._status === 'inactive' ? (
                          <button
                            className="ob-btn ob-btn--sm ob-btn--icon-only"
                            style={{ color: '#9aa3b0', borderColor: '#e4e9f0', background: '#f4f5f7', cursor: 'not-allowed', opacity: 0.6 }}
                            disabled
                            title={`Already inactive — delete disabled because ${usedCount} run(s) exist`}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="21 8 21 21 3 21 3 8" />
                              <rect x="1" y="3" width="22" height="5" />
                              <path d="M9 15l3 3 3-3" />
                              <line x1="12" y1="12" x2="12" y2="18" />
                            </svg>
                          </button>
                        ) : (
                          <button
                            className="ob-btn ob-btn--sm ob-btn--icon-only"
                            style={{ color: '#f59e0b', borderColor: '#fcd34d', background: '#fef3c7' }}
                            onClick={() => handleSetStatus(flow.id, 'inactive')}
                            disabled={isBusy}
                            title={`Archive this flow`}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="21 8 21 21 3 21 3 8" />
                              <rect x="1" y="3" width="22" height="5" />
                              <path d="M9 15l3 3 3-3" />
                              <line x1="12" y1="12" x2="12" y2="18" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {!loading && pagination.total > 0 && (
        <div className="pagination d-flex flex-wrap align-items-center justify-content-between">
          <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
            <div className="d-flex flex-wrap" style={{ gap: '20px' }}>
              <div className="pagination-info d-flex align-items-center">
                <span style={{ color: '#525B66' }}>
                  Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, pagination.total)} of <strong>{pagination.total}</strong> Flows
                </span>
              </div>
              <div className="pagination-buttons">
                {renderCustomPagination(totalPages, currentPage, setCurrentPage)}
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span style={{ color: '#525B66', lineHeight: '100%' }}>Flows per page</span>&nbsp;&nbsp;&nbsp;&nbsp;
              <select
                value={pageSize}
                onChange={e => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
                style={{ width: '46px', height: '32px', borderRadius: '2px', border: '1px solid #8A9199', color: '#8A9199' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="ob-modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="ob-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 8 }}>Delete Flow?</h3>
            <p style={{ fontSize: 13, color: '#525b66', marginBottom: 20 }}>
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="ob-btn" onClick={() => setDeleteId(null)}>Cancel</button>
              <button
                className="ob-btn ob-btn--primary"
                style={{ background: '#ef4444', borderColor: '#ef4444' }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowsList;
